import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ROJO, LILA, descargarBlob, cabeceraExcel } from './cabeceraFmds';
import { COLOR_MODALIDAD } from './descargaJornadas';

const SETS_EDITABLES = [
  'Set 1 Local', 'Set 1 Visitante',
  'Set 2 Local', 'Set 2 Visitante',
  'Set 3 Local', 'Set 3 Visitante',
];

// Fila de la hoja donde empieza la tabla (debajo de la cabecera con logo).
// La importación de resultados la busca por su primera celda, no por número.
export const FILA_CABECERA_TABLA = 8;

const borde = { style: 'thin', color: { argb: 'FFB8B0CC' } };
const bordes = { top: borde, bottom: borde, left: borde, right: borde };

// Hoja con la cabecera de la federación y una tabla de columnas = claves de
// `filas`. `editables` son las columnas desbloqueadas (si se pasa, se protege).
async function tablaExcelEstilada(filas, nombreHoja, nombreArchivo, temporada, editables) {
  const libro = new ExcelJS.Workbook();
  const hoja = libro.addWorksheet(nombreHoja, { views: [{ showGridLines: false }] });
  const cabeceras = Object.keys(filas[0]);
  cabeceras.forEach((h, i) => {
    const maxContenido = Math.max(...filas.map(f => String(f[h] ?? '').length));
    hoja.getColumn(i + 1).width = Math.max(h.length, maxContenido) + 3;
  });
  await cabeceraExcel(libro, hoja, temporada, cabeceras.length);

  const cab = hoja.getRow(FILA_CABECERA_TABLA);
  cab.height = 20;
  cabeceras.forEach((h, i) => {
    const celda = cab.getCell(i + 1);
    celda.value = h;
    celda.font = { name: 'Arial', size: 10, bold: true, color: { argb: ROJO } };
    celda.alignment = { horizontal: 'center', vertical: 'middle' };
    celda.border = bordes;
    celda.protection = { locked: true };
  });

  filas.forEach((f, n) => {
    const r = hoja.getRow(FILA_CABECERA_TABLA + 1 + n);
    r.height = 20;
    cabeceras.forEach((h, i) => {
      const celda = r.getCell(i + 1);
      const editable = editables?.includes(h);
      celda.value = f[h];
      celda.font = { name: 'Arial', size: 10 };
      celda.alignment = { horizontal: 'left', vertical: 'middle' };
      // Amarillo claro = celda editable; si no, el color de su modalidad.
      celda.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: editable ? 'FFFFF9C4' : (COLOR_MODALIDAD[f.Modalidad] ?? LILA) },
      };
      celda.border = bordes;
      celda.protection = { locked: !editable };
    });
  });

  if (editables) {
    // Bloquea celdas locked, permite editar unlocked
    await hoja.protect('', {
      selectLockedCells: true,
      selectUnlockedCells: true,
      insertRows: false,
      deleteRows: false,
      insertColumns: false,
      deleteColumns: false,
    });
  }

  const buffer = await libro.xlsx.writeBuffer();
  descargarBlob(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    nombreArchivo,
  );
}

export async function descargarPartidosExcel(filas, nombreHoja, nombreArchivo, temporada) {
  if (!filas.length) return;
  await tablaExcelEstilada(filas, nombreHoja, nombreArchivo, temporada, SETS_EDITABLES);
}

export async function descargarParticipantesExcel(filas, nombreHoja, nombreArchivo, temporada) {
  if (!filas.length) return;
  await tablaExcelEstilada(filas, nombreHoja, nombreArchivo, temporada);
}

export function descargarExcel(filas, nombreHoja, nombreArchivo) {
  if (!filas.length) return;
  const hoja = XLSX.utils.json_to_sheet(filas);
  const libro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(libro, hoja, nombreHoja);
  XLSX.writeFile(libro, nombreArchivo);
}

export function descargarTablaPDF(cabeceras, filas, titulo, nombreArchivo) {
  const doc = new jsPDF({ orientation: filas.length && cabeceras.length > 5 ? 'landscape' : 'portrait' });
  doc.setFontSize(13);
  doc.text(titulo, 14, 15);
  autoTable(doc, {
    head: [cabeceras],
    body: filas,
    startY: 22,
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [26, 34, 56], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 247, 250] },
  });
  doc.save(nombreArchivo);
}

export function descargarCSV(filas, nombreArchivo) {
  if (!filas.length) return;
  const cols = Object.keys(filas[0]);
  const csv = [
    cols.join(','),
    ...filas.map(f =>
      cols.map(k => `"${String(f[k] ?? '').replace(/"/g, '""')}"`).join(',')
    ),
  ].join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }));
  a.download = nombreArchivo;
  a.click();
  URL.revokeObjectURL(a.href);
}
